import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { Button } from '@/components/ui/button';
import { canOfferAct } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import type { Provider, ProviderListParams } from '@/lib/api/domains/providers';
import { providerListQuery } from '../api/provider-queries';
import { providerColumns } from './provider-columns';

export function ProviderTable({
  params,
  onPageChange,
  onRowClick,
  onEdit,
}: {
  params: ProviderListParams;
  onPageChange: (page: number) => void;
  onRowClick: (provider: Provider) => void;
  onEdit?: (provider: Provider) => void;
}) {
  const query = useQuery(providerListQuery(params));
  const session = useAuthStore((s) => s.session);
  // The per-row edit is the F04 entity-edit act (Administrator-only), read
  // per-act from the server-sourced capabilities — a `tpabilling_user` (Viewer)
  // holds the read, not this act, so the control is withheld (E02.F04.R5,
  // AC-9311d02c). Courtesy only; the domain gate refuses regardless.
  const showEdit =
    Boolean(onEdit) && canOfferAct(session, 'tpabilling.enrollment.edit');

  return (
    <DataTable<Provider>
      columns={providerColumns}
      data={query.data?.items ?? []}
      getRowId={(row) => row.id}
      tableId="providers"
      actions={
        showEdit
          ? (provider) => (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${provider.name}`}
                onClick={() => onEdit?.(provider)}
              >
                <Pencil className="size-4" />
              </Button>
            )
          : undefined
      }
      isLoading={query.isLoading}
      isError={query.isError}
      errorMessage="Failed to load providers."
      emptyTitle="No providers found"
      page={query.data?.page ?? params.page ?? 1}
      pageSize={query.data?.pageSize ?? params.pageSize ?? 25}
      total={query.data?.total ?? 0}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
    />
  );
}
